import useConversationDraft from '../hooks/useConversationDraft';
import useWhatsappViewport from '../hooks/useWhatsappViewport';
import { dayKeyOf, dayLabelOf, listTimeOf } from '../utils/whatsappDates';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { BsSearch, BsThreeDotsVertical, BsPaperclip, BsEmojiSmile, BsFillSendFill, BsCheck2, BsCheck2All, BsClock, BsArrowRepeat, BsDownload, BsPlay, BsFileEarmark, BsImage, BsCameraVideo, BsVolumeUp, BsChevronLeft, BsReply, BsExclamationCircle, BsFileText, BsChevronDown, BsChevronUp, BsPersonLinesFill, BsLink45Deg, BsRobot, BsGear, BsTrash } from 'react-icons/bs';
import { FiLink } from 'react-icons/fi';
import { apiFetch, parseJsonSafe, API_URL } from '../utils/api';
import { formatConversationStatus } from '../utils/whatsapp';
import { EMOJI_CATEGORIES, FREQUENT_EMOJIS } from './whatsappEmojis';
import './WhatsappPage.css';
import WhatsappQrPanel from '../components/WhatsappQrPanel';

// Estados de IA ya resueltos: un mensaje en cualquiera de ellos NO debe mostrarse como
// "Borrador IA" pendiente (incluye 'enviado_auto', que el auto-envío ya mandó).
const IA_DRAFT_RESOLVED = ['aprobado', 'rechazado', 'enviado_auto'];
// Solo mensajes ENTRANTES pueden tener Borrador IA pendiente: un eco de un mensaje propio
// (coexistencia con la app del móvil) jamás debe abrir la ventana del borrador.
// Los parámetros de plantilla de WhatsApp NO admiten saltos de línea, tabuladores ni más
// de 4 espacios seguidos: Meta los rechaza. Se aplana a una línea con " · " (mismo criterio
// que el backend) para poder mostrar aquí exactamente lo que recibirá el cliente.
const flattenParam = (v) => String(v == null ? '' : v)
  .replace(/\r\n?/g, '\n').split('\n').map((l) => l.trim()).filter(Boolean).join(' · ')
  .replace(/\t+/g, ' ').replace(/ {2,}/g, ' ').trim();

const isPendingDraft = (m) => m?.direccion === 'in' && (m?.ai_suggested_body || '').toString().trim() && !IA_DRAFT_RESOLVED.includes(m?.ia_status);

// La pestaña está realmente "a la vista" (para no marcar leído ni robar scroll en segundo plano).
const isTabActive = () => (typeof document === 'undefined') || (document.visibilityState === 'visible' && document.hasFocus());

const PUSH_VAPID_KEY_STORAGE = 'waPushVapidKey';

const urlBase64ToUint8Array = (base64String = '') => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Notas de voz transcritas por el backend: el cuerpo llega como «[Audio transcrito] texto».
const TRANSCRIPT_PREFIX_RE = /^\[audio transcrito\]\s*/i;
const getTranscriptText = (value) => {
  const text = (value || '').toString().trim();
  return TRANSCRIPT_PREFIX_RE.test(text) ? text.replace(TRANSCRIPT_PREFIX_RE, '').trim() : '';
};

const getMediaPlaceholderKind = (value) => {
  const text = (value || '').toString().trim().toLowerCase();
  if (!text) return null;
  if (text === '[imagen]' || text === '[image]' || text === '[photo]' || text === '[foto]') return 'image';
  if (text === '[audio]' || text === '[voice]') return 'audio';
  if (text === '[video]' || text === '[vídeo]') return 'video';
  if (text === '[documento]' || text === '[document]' || text === '[archivo]' || text === '[file]') return 'document';
  if (text === '[contacts]' || text === '[contact]' || text === '[contacto]' || text === '[contactos]') return 'contacts';
  return null;
};

const getMediaPlaceholderLabel = (kind) => {
  if (kind === 'image') return 'Imagen';
  if (kind === 'audio') return 'Audio';
  if (kind === 'video') return 'Vídeo';
  if (kind === 'document') return 'Documento';
  if (kind === 'contacts') return 'Contacto compartido';
  return '';
};

const parseRawPayload = (rawPayload) => {
  if (!rawPayload) return null;
  if (typeof rawPayload === 'object') return rawPayload;
  if (typeof rawPayload !== 'string') return null;
  try {
    return JSON.parse(rawPayload);
  } catch (_e) {
    return null;
  }
};

const getContactSummaryFromPayload = (rawPayload) => {
  const payload = parseRawPayload(rawPayload);
  const contacts = Array.isArray(payload?.contacts) ? payload.contacts : [];
  if (!contacts.length) return '';
  const first = contacts[0] || {};
  const name = (first?.name?.formatted_name
    || [first?.name?.first_name, first?.name?.middle_name, first?.name?.last_name].filter(Boolean).join(' ')
    || first?.org?.company
    || '').toString().trim();
  const phone = (Array.isArray(first?.phones)
    ? (first.phones.find((p) => p?.phone || p?.wa_id)?.phone || first.phones.find((p) => p?.phone || p?.wa_id)?.wa_id || '')
    : '').toString().trim();
  const base = [name, phone ? `(${phone})` : ''].filter(Boolean).join(' ').trim() || 'Contacto compartido';
  if (contacts.length > 1) return `${base} +${contacts.length - 1}`;
  return base;
};

const prettifyMediaPlaceholderText = (value, options = {}) => {
  const kind = getMediaPlaceholderKind(value);
  if (kind === 'contacts' && options.contactSummary) return options.contactSummary;
  return kind ? getMediaPlaceholderLabel(kind) : (value || '');
};

// Placeholder genérico tipo "[button]" / "[interactive]" que el backend antiguo
// guardaba cuando no sabía interpretar el mensaje.
const isGenericBracketPlaceholder = (value) => /^\[[a-z_]+\]$/i.test((value || '').toString().trim());

// Recupera el texto real de una respuesta de botón/lista desde el payload de Meta.
// Sirve para mostrar bien mensajes antiguos guardados como "[button]".
const getButtonTextFromPayload = (rawPayload) => {
  const payload = parseRawPayload(rawPayload);
  if (!payload) return '';
  if (payload.button) {
    return (payload.button.text || payload.button.payload || '').toString().trim();
  }
  const reply = payload.interactive?.button_reply || payload.interactive?.list_reply;
  if (reply) return (reply.title || reply.id || '').toString().trim();
  return '';
};

const normalizeDriveMediaUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (!raw.includes('drive.google.com') && !raw.includes('drive.usercontent.google.com')) return raw;
  const match = raw.match(/\/file\/d\/([^/]+)/) || raw.match(/[?&]id=([^&]+)/);
  if (!match) return raw;
  return `/api/uploads/drive/${match[1]}`;
};

const toAbsoluteMediaUrl = (value = '') => {
  const normalized = normalizeDriveMediaUrl(value);
  if (!normalized) return '';
  if (
    normalized.startsWith('http') ||
    normalized.startsWith('//') ||
    normalized.startsWith('blob:') ||
    normalized.startsWith('data:')
  ) {
    return normalized;
  }
  if (API_URL) return `${API_URL}${normalized}`;
  if (typeof window !== 'undefined') {
    try {
      return new URL(normalized, window.location.origin).toString();
    } catch (_e) {
      // fallback below
    }
  }
  return normalized;
};

const sanitizeDownloadFilename = (value = '') => {
  return String(value || '')
    .trim()
    .replace(/[/\\?%*:|"<>]/g, '_')
    .replace(/\s+/g, ' ')
    .slice(0, 120);
};

const buildMediaDownloadUrl = (value, filename = '') => {
  const absolute = toAbsoluteMediaUrl(value);
  if (!absolute) return '';
  try {
    const parsed = new URL(absolute, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    if (!/\/api\/uploads\/drive\/[a-zA-Z0-9_-]+$/i.test(parsed.pathname)) return absolute;
    parsed.searchParams.set('download', '1');
    const safeName = sanitizeDownloadFilename(filename);
    if (safeName) parsed.searchParams.set('filename', safeName);
    if (/^https?:\/\//i.test(absolute)) return parsed.toString();
    return `${parsed.pathname}${parsed.search}`;
  } catch (_e) {
    return absolute;
  }
};

// iOS/Safari no reproduce OGG/Opus (formato de las notas de voz de WhatsApp). Detectamos soporte una sola vez.
const OGG_OPUS_PLAYABLE = (() => {
  if (typeof document === 'undefined') return true;
  try {
    const probe = document.createElement('audio');
    if (!probe || typeof probe.canPlayType !== 'function') return true;
    return Boolean(probe.canPlayType('audio/ogg; codecs=opus') || probe.canPlayType('audio/ogg'));
  } catch (_e) {
    return true;
  }
})();

const isOggOrOpusMime = (mime = '') => /ogg|opus/i.test(String(mime || ''));
const isDriveProxyUrl = (url = '') => /\/api\/uploads\/drive\/[a-zA-Z0-9_-]+(\?|$)/i.test(String(url || ''));

// Si el navegador no soporta OGG/Opus, pedimos al backend la versión MP3 transcodificada.
const buildPlayableAudioSource = (mediaUrl, mime = '') => {
  // Sin "type" el navegador detecta el formato por el contenido (comportamiento anterior).
  if (!mediaUrl) return { src: '', type: undefined };
  if (OGG_OPUS_PLAYABLE || !isOggOrOpusMime(mime) || !isDriveProxyUrl(mediaUrl)) {
    return { src: mediaUrl, type: undefined };
  }
  const separator = mediaUrl.includes('?') ? '&' : '?';
  return { src: `${mediaUrl}${separator}format=mp3`, type: 'audio/mpeg' };
};

const WhatsappCloudPage = () => {
  const token = localStorage.getItem('token');
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 900 : false));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chats, setChats] = useState([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedConversationId, setSelectedConversationId] = useState('');
  const [messages, setMessages] = useState([]);
  const composerKey = selectedConversationId ? `conversation:${selectedConversationId}` : (selectedContact ? `phone:${selectedContact}` : '');
  const [composer, setComposer] = useConversationDraft(composerKey, localStorage.getItem('userEmail') || 'staff');
  const [search, setSearch] = useState('');
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  const [draft, setDraft] = useState({ id: null, text: '', actions: [], selected: [] });
  // Acciones propuestas por la IA junto al borrador (tarea, evento, enviar factura/presupuesto, ticket)
  const parseDraftActions = (raw) => {
    if (!raw) return [];
    try {
      const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(arr) ? arr.filter((a) => a && a.type) : [];
    } catch (_e) { return []; }
  };
  const ACTION_LABELS = {
    create_task: 'Crear tarea', create_event: 'Crear evento en la agenda', update_event: 'Actualizar evento', delete_event: 'Eliminar evento',
    send_invoices: 'Enviar factura(s) por WhatsApp', send_invoice: 'Enviar factura por WhatsApp', send_budget: 'Enviar presupuesto por WhatsApp', create_support_ticket: 'Abrir ticket de soporte',
    create_dev_request: 'Crear cambio solicitado por el cliente'
  };
  const describeAction = (a) => {
    const base = ACTION_LABELS[a.type] || a.type;
    if (a.type === 'create_task') return `${base}: ${a.title || ''}${a.importance ? ` (${a.importance})` : ''}`;
    if (a.type === 'create_event' || a.type === 'update_event') return `${base}: ${a.summary || ''}${a.date ? ` · ${a.date}${a.time ? ` ${a.time}` : ''}` : ''}`;
    if (a.type === 'send_invoices' || a.type === 'send_invoice') return `${base}: ${Array.isArray(a.refs) && a.refs.length ? a.refs.join(', ') : (a.numero || `últimas ${a.last || 1}${a.year ? ` de ${a.year}` : ''}`)}`;
    if (a.type === 'send_budget') return `${base}: ${a.numero || a.budgetId || ''}`;
    if (a.type === 'create_support_ticket') return `${base}: ${a.subject || ''}${a.priority ? ` (${a.priority})` : ''}`;
    if (a.type === 'create_dev_request') return `${base}: ${a.title || ''}${a.project ? ` · ${a.project}` : ''}`;
    return base;
  };
  const [replyTarget, setReplyTarget] = useState(null);
  const [linkModal, setLinkModal] = useState({
    open: false,
    conversationId: '',
    phone: '',
    contactName: '',
    clientId: '',
    search: '',
    clients: [],
    loading: false,
    saving: false,
    error: ''
  });
  const [sendingMenu, setSendingMenu] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplateLang, setSelectedTemplateLang] = useState('es');
  // Plantillas META con variables: se rellenan en un modal antes de enviar.
  const [templateModal, setTemplateModal] = useState({ open: false, tpl: null, values: {}, sending: false, error: '' });
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  // Móvil: selector de plantillas (en escritorio va en la cabecera) y borrador IA plegable
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [draftCollapsed, setDraftCollapsed] = useState(false);
  const [globalMenuOpen, setGlobalMenuOpen] = useState(false);
  const [pushPermission, setPushPermission] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });
  const [pushSubscribing, setPushSubscribing] = useState(false);
  const [pushReady, setPushReady] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [aiConfig, setAiConfig] = useState({ generateDraft: true, autoSend: false });
  const [chatOptionsModal, setChatOptionsModal] = useState({
    open: false,
    conversationId: '',
    disable_auto_reply: false,
    ai_generate: true,
    ai_auto_send: false,
    loading: false
  });
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesAbortRef = useRef(null);
  const messagesRequestRef = useRef(0);
  const composerRef = useRef(null);        // textarea, para insertar emoji en la posición del cursor
  const emojiRef = useRef(null);           // popover de emojis, para cerrar al hacer click fuera
  const messagesScrollRef = useRef(null);  // contenedor scrollable de mensajes
  const nearBottomRef = useRef(true);      // ¿el usuario está pegado al fondo? (para no robarle el scroll)
  const draftDirtyRef = useRef(false);     // ¿el usuario ha editado el borrador? (para no pisarlo al refrescar)
  const scrollConvRef = useRef(null);      // última conversación para la que ajustamos el scroll

  // Selector de emojis: categoría activa
  const [emojiCat, setEmojiCat] = useState('frequent');
  const navigate = useNavigate();
  const location = useLocation();

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const filteredChats = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return chats;
    return chats.filter((c) => c.name.toLowerCase().includes(term) || c.contact.toLowerCase().includes(term) || (c.lastMessage || '').toLowerCase().includes(term));
  }, [search, chats]);

  const selectedChat = useMemo(() => {
    if (selectedConversationId) return chats.find((c) => String(c.conversationId) === String(selectedConversationId)) || null;
    if (selectedContact) return chats.find((c) => c.contact === selectedContact) || null;
    return null;
  }, [chats, selectedConversationId, selectedContact]);
  const displayName = selectedChat ? (selectedChat.contact_name ? `${selectedChat.contact_name} - ${selectedChat.name || selectedChat.contact}` : (selectedChat.name || selectedChat.contact)) : '';
  const latestAiMsg = useMemo(() => {
    return [...messages]
      .filter(isPendingDraft)
      .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0))[0] || null;
  }, [messages]);
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t) => t.id === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);
  const templateLanguageOptions = useMemo(() => {
    if (!selectedTemplate || selectedTemplate.type === 'custom') return ['es', 'es_ES', 'es_LA'];
    const lang = (selectedTemplate.language || 'es').trim();
    return [lang || 'es'];
  }, [selectedTemplate]);
  const messageByWaId = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      const waId = m.wa_message_id;
      if (waId) map.set(waId, m);
    });
    return map;
  }, [messages]);
  const resolveMessagePreview = (msg) => {
    if (!msg) return '';
    const raw = (msg.cuerpo_texto || msg.text || '').toString();
    const tipo = msg.tipo || msg.type || '';
    if (tipo === 'reaction') return raw || 'Reacción';
    const transcriptPreview = getTranscriptText(raw);
    if (transcriptPreview) {
      const t = `🎙️ ${transcriptPreview}`;
      return t.length > 90 ? `${t.slice(0, 90)}…` : t;
    }
    const contactSummary = getContactSummaryFromPayload(msg.raw_payload);
    let label = prettifyMediaPlaceholderText(raw, { contactSummary });
    if (!label || isGenericBracketPlaceholder(label)) {
      label = getButtonTextFromPayload(msg.raw_payload) || (isGenericBracketPlaceholder(label) ? '' : label);
    }
    const base = label || (msg.media_local_url || msg.media_url ? 'Archivo adjunto' : '');
    if (!base) return '';
    return base.length > 90 ? `${base.slice(0, 90)}…` : base;
  };

  const fetchChats = async ({ silent = false } = {}) => {
    if (!silent) setLoadingChats(true);
    try {
      const res = await apiFetch('/api/whatsapp/conversations', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) {
        setChats(data);
        // El deep-link y la auto-selección SOLO en cargas no silenciosas: el polling usa una
        // clausura con el selectedContact del primer render (vacío); si corriera aquí,
        // re-seleccionaría data[0] cada pocos segundos y secuestraría la conversación abierta.
        if (!silent) {
          const params = new URLSearchParams(location.search);
          const deepChatId = params.get('chatId');
          if (deepChatId) {
            const target = data.find((c) => String(c.conversationId) === String(deepChatId));
            if (target) {
              setSelectedContact(target.contact);
              setSelectedConversationId(target.conversationId);
              return;
            }
          }
          if (!isMobile && !selectedContact && !selectedConversationId && data[0]) {
            setSelectedContact(data[0].contact);
            setSelectedConversationId(data[0].conversationId);
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoadingChats(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await apiFetch('/api/whatsapp/templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (Array.isArray(data)) {
        setTemplates(data);
        if (!selectedTemplateId && data[0]) {
          setSelectedTemplateId(data[0].id);
          setSelectedTemplateLang(data[0].language || 'es');
        }
      }
    } catch (err) {
      console.error('Error cargando plantillas', err);
    }
  };

  const fetchAiConfig = async () => {
    try {
      const res = await apiFetch('/api/whatsapp/ai-config', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && data) {
        setAiConfig({ generateDraft: data.generateDraft !== false, autoSend: data.autoSend === true });
      }
    } catch (err) {
      console.error('Error cargando config IA', err);
    }
  };

  // silent=true → refresco por polling/SSE: sin spinner, sin robar el scroll y sin pisar
  // el borrador que el usuario esté editando (draftDirtyRef).
  const fetchMessages = async (contact, conversationId, { silent = false } = {}) => {
    if (!contact && !conversationId) return;
    const requestId = messagesRequestRef.current + 1;
    messagesRequestRef.current = requestId;
    if (messagesAbortRef.current) {
      messagesAbortRef.current.abort();
    }
    const controller = new AbortController();
    messagesAbortRef.current = controller;
    if (!silent) setLoadingMessages(true);
    try {
      const qs = new URLSearchParams();
      if (contact) qs.append('contact', contact);
      if (conversationId) qs.append('conversationId', conversationId);
      const res = await apiFetch(`/api/whatsapp/messages?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      const data = await parseJsonSafe(res);
      if (controller.signal.aborted || requestId !== messagesRequestRef.current) return;
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) {
        // Mide si el usuario está pegado al fondo ANTES de re-renderizar (para no robarle el scroll).
        const cont = messagesScrollRef.current;
        if (cont) nearBottomRef.current = (cont.scrollHeight - cont.scrollTop - cont.clientHeight) < 140;
        setMessages(data);
        const latestAi = [...data]
          .filter(isPendingDraft)
          .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0))[0];
        // No pisar la edición en curso del borrador: solo actualizar si cambió el mensaje
        // de origen o el usuario no lo ha tocado.
        setDraft((prev) => {
          if (!latestAi) { draftDirtyRef.current = false; return { id: null, text: '', actions: [], selected: [] }; }
          if (prev.id === latestAi.id && draftDirtyRef.current) return prev;
          draftDirtyRef.current = false;
          const actions = parseDraftActions(latestAi.ai_actions);
          return { id: latestAi.id, text: latestAi.ai_suggested_body || '', actions, selected: actions.map((_a, i) => i) };
        });
        // Marcar leído solo si la conversación está realmente a la vista (pestaña visible y con foco).
        if (isTabActive()) {
          const latestTs = data.length ? data[data.length - 1]?.fecha : null;
          markConversationSeen(conversationId, latestTs);
        }
      } else if (!silent) {
        setMessages([]);
        setDraft({ id: null, text: '' });
        draftDirtyRef.current = false;
      }
    } catch (err) {
      if (controller.signal.aborted || err?.name === 'AbortError') return;
      console.error(err);
      if (!silent) {
        setMessages([]);
        setDraft({ id: null, text: '' });
        draftDirtyRef.current = false;
      }
    } finally {
      // Apagar el spinner según el request GANADOR (sin condicionar a !silent): si un fetch
      // silencioso aborta a uno no silencioso y luego gana, debe limpiar el spinner que aquel
      // dejó encendido; si no, "Cargando mensajes…" se quedaría colgado para siempre.
      if (requestId === messagesRequestRef.current) {
        setLoadingMessages(false);
      }
    }
  };

  const syncPushSubscription = async ({ requestPermission = false } = {}) => {
    if (!token) return false;
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushPermission('unsupported');
      setPushReady(false);
      return false;
    }
    let permission = Notification.permission;
    if (requestPermission && permission !== 'granted') {
      permission = await Notification.requestPermission();
    }
    setPushPermission(permission);
    if (permission !== 'granted') {
      setPushReady(false);
      localStorage.removeItem(PUSH_VAPID_KEY_STORAGE);
      return false;
    }

    setPushSubscribing(true);
    try {
      const keyRes = await apiFetch('/api/push/public-key', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const keyData = await parseJsonSafe(keyRes);
      if (keyRes.status === 401) {
        handleUnauthorized();
        return false;
      }
      if (!keyRes.ok || !keyData?.publicKey) {
        throw new Error(keyData.message || 'No se pudo obtener la clave de notificaciones');
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      const storedVapidKey = localStorage.getItem(PUSH_VAPID_KEY_STORAGE) || '';
      if (subscription && (!storedVapidKey || storedVapidKey !== keyData.publicKey)) {
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
      }
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyData.publicKey)
        });
      }

      const subRes = await apiFetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON ? subscription.toJSON() : subscription })
      });
      const subData = await parseJsonSafe(subRes);
      if (subRes.status === 401) {
        handleUnauthorized();
        return false;
      }
      if (!subRes.ok) throw new Error(subData.message || 'No se pudo registrar la suscripción push');
      localStorage.setItem(PUSH_VAPID_KEY_STORAGE, keyData.publicKey);
      setPushReady(true);
      return true;
    } catch (err) {
      console.error('Error activando push', err);
      setPushReady(false);
      if (requestPermission) setStatus(err.message || 'No se pudieron activar las notificaciones push');
      return false;
    } finally {
      setPushSubscribing(false);
    }
  };

  const sendPushTest = async () => {
    if (!token) return;
    if (!pushReady) {
      const ok = await syncPushSubscription({ requestPermission: true });
      if (!ok) return;
    }
    try {
      const res = await apiFetch('/api/push/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar push de prueba');
      setStatus('Push de prueba enviado');
    } catch (err) {
      setStatus(err.message || 'No se pudo enviar push de prueba');
    }
  };

  useWhatsappViewport({ chatOpen: isMobile && !sidebarOpen, messagesRef: messagesScrollRef, nearBottomRef });

  // Los avisos (enviado, error…) desaparecen solos para no ocupar sitio bajo el campo de escribir
  useEffect(() => {
    if (!status) return undefined;
    const t = setTimeout(() => setStatus(''), 7000);
    return () => clearTimeout(t);
  }, [status]);

  // Cada borrador nuevo de la IA se muestra desplegado
  useEffect(() => { setDraftCollapsed(false); }, [draft.id]);

  // Al plegar/desplegar el borrador o citar un mensaje cambia el alto de la zona de mensajes:
  // si el usuario estaba al fondo, se queda al fondo
  useEffect(() => {
    const cont = messagesScrollRef.current;
    if (!cont || !nearBottomRef.current) return;
    window.requestAnimationFrame(() => { cont.scrollTop = cont.scrollHeight; });
  }, [draftCollapsed, draft.id, replyTarget]);

  useEffect(() => {
    fetchChats();
    fetchTemplates();
    fetchAiConfig();
    const handleResize = () => {
      const mobile = window.innerWidth < 900;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof Notification === 'undefined') {
      setPushPermission('unsupported');
      setPushReady(false);
      return;
    }
    setPushPermission(Notification.permission);
    if (Notification.permission === 'granted') {
      syncPushSubscription({ requestPermission: false });
    } else {
      setPushReady(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const tpl = selectedTemplate;
    if (tpl?.language) {
      setSelectedTemplateLang(tpl.language);
    }
  }, [selectedTemplateId, selectedTemplate]);

  useEffect(() => {
    if (selectedContact || selectedConversationId) fetchMessages(selectedContact, selectedConversationId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact, selectedConversationId]);

  useEffect(() => {
    setReplyTarget(null);
  }, [selectedConversationId]);

  useEffect(() => {
    const convChanged = scrollConvRef.current !== selectedConversationId;
    scrollConvRef.current = selectedConversationId;
    if (!messagesEndRef.current) return;
    // Al cambiar de chat, siempre al fondo. En un refresco (mismo chat), solo bajamos si el
    // usuario ya estaba pegado al fondo: si está leyendo el historial, no le robamos el scroll.
    if (convChanged) nearBottomRef.current = true;
    if (convChanged || nearBottomRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: convChanged ? 'instant' : 'smooth', block: 'end' });
    }
  }, [messages, selectedConversationId]);

  useEffect(() => {
    return () => {
      if (messagesAbortRef.current) messagesAbortRef.current.abort();
    };
  }, []);

  // ── Auto-refresco de respaldo: polling silencioso + refresco al volver a la pestaña ──
  // El SSE es el canal principal, pero puede caerse (proxy, red). Este polling garantiza
  // que la conversación abierta y la lista de chats se refresquen "solas".
  useEffect(() => {
    const tick = () => {
      if (!isTabActive()) return; // no gastar red en segundo plano
      if (selectedContactRef.current || selectedConversationIdRef.current) {
        fetchMessages(selectedContactRef.current, selectedConversationIdRef.current, { silent: true });
      }
      fetchChats({ silent: true });
    };
    const interval = setInterval(tick, 12000);
    const onVisible = () => { if (isTabActive()) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizePhone = (val = '') => (val || '').replace(/\D+/g, '');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const phoneParam = normalizePhone(params.get('phone') || '');
    const contactName = params.get('name') || '';
    if (!phoneParam) return;
    (async () => {
      try {
        const res = await apiFetch('/api/whatsapp/conversations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ phone: phoneParam, contactName })
        });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) throw new Error(data.message || 'No se pudo crear conversación');
        setSelectedContact(data.phone || phoneParam);
        setSelectedConversationId(data.id);
        if (isMobile) setSidebarOpen(false);
        setChats((prev) => {
          const exists = prev.find((c) => c.conversationId === data.id || c.contact === (data.phone || phoneParam));
          if (exists) return prev;
          return [
            {
              conversationId: data.id,
              contact: data.phone || phoneParam,
              name: data.phone || phoneParam,
              client_id: data.client_id || null,
              contact_name: contactName || null,
              status: 'abierta',
              lastMessage: '',
              lastDate: null,
              direction: 'out',
              unread: 0
            },
            ...prev
          ];
        });
        fetchChats();
        fetchMessages(data.phone || phoneParam, data.id);
      } catch (err) {
        console.error('Error iniciando conversación', err);
        setStatus(err.message);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isMobile]);

  const persistLastSeen = (updater) => {
    const raw = localStorage.getItem('waLastSeen') || '{}';
    let prev = {};
    try {
      prev = JSON.parse(raw);
    } catch (_e) {
      prev = {};
    }
    const next = typeof updater === 'function' ? updater(prev) : updater;
    localStorage.setItem('waLastSeen', JSON.stringify(next));
  };

  const clearUnread = (conversationId, lastDateOverride) => {
    if (!conversationId) return;
    const ts = lastDateOverride ? new Date(lastDateOverride).toISOString() : new Date().toISOString();
    persistLastSeen((prev) => ({ ...prev, [conversationId]: ts }));
    setChats((prev) =>
      prev.map((c) =>
        c.conversationId === conversationId ? { ...c, unread: 0, lastDate: c.lastDate || ts } : c
      )
    );
  };

  const markConversationSeen = async (conversationId, lastDate) => {
    if (!conversationId) return;
    const ts = lastDate ? new Date(lastDate).toISOString() : new Date().toISOString();
    persistLastSeen((prev) => ({ ...prev, [conversationId]: ts }));
    try {
      await apiFetch(`/api/whatsapp/conversations/${conversationId}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ timestamp: ts })
      });
    } catch (_err) {
      // best-effort
    }
  };

  // SSE refs to access latest values inside the event listener without re-creating the connection
  const selectedConversationIdRef = useRef(selectedConversationId);
  const selectedContactRef = useRef(selectedContact);
  useEffect(() => { selectedConversationIdRef.current = selectedConversationId; }, [selectedConversationId]);
  useEffect(() => { selectedContactRef.current = selectedContact; }, [selectedContact]);

  useEffect(() => {
    if (!token) return;
    let es = null;
    let reconnectTimer = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      es = new EventSource(`/api/whatsapp/stream?token=${encodeURIComponent(token)}`);
      // Al (re)abrir la conexión, re-sincronizar: recupera lo que llegó mientras estuvo caída.
      es.addEventListener('open', () => {
        fetchChats({ silent: true });
        if (selectedContactRef.current || selectedConversationIdRef.current) {
          fetchMessages(selectedContactRef.current, selectedConversationIdRef.current, { silent: true });
        }
      });
      es.addEventListener('incoming', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const curConvId = selectedConversationIdRef.current;
          const curContact = selectedContactRef.current;
          if (payload.conversationId === curConvId || payload.from === curContact) {
            fetchMessages(curContact, curConvId, { silent: true });
            if (isTabActive()) markConversationSeen(payload.conversationId, payload.timestamp);
          }
          fetchChats({ silent: true });
          if (!pushReady && typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.visibilityState === 'hidden') {
            const title = `WhatsApp: ${payload.from || 'Nuevo mensaje'}`;
            const body = prettifyMediaPlaceholderText(payload.body || 'Nuevo mensaje');
            const n = new Notification(title, { body: body || 'Nuevo mensaje' });
            n.onclick = () => window.focus();
          }
        } catch (_e) {
          // ignore parse errors
        }
      });
      es.addEventListener('error', () => {
        es.close();
        if (!stopped) reconnectTimer = setTimeout(connect, 3000);
      });
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      if (es) es.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pushReady]);

  const sendMessage = async () => {
    if (!selectedChat) return;
    if (sending || uploadingFile) return; // evita doble/triple envío (Enter repetido)
    const text = composer.trim();
    if (!text && !pendingFile) return;
    const replyTo = replyTarget?.wa_message_id || '';
    if (pendingFile) {
      setSending(true);
      setStatus('');
      nearBottomRef.current = true; // al enviar propio (adjunto), bajamos al fondo
      const sent = await uploadAndSendMedia(pendingFile, text || undefined, replyTo);
      if (sent) {
        setPendingFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (text) setComposer('');
        setReplyTarget(null);
        // Responder (aunque sea con adjunto) descarta el Borrador IA pendiente.
        setDraft({ id: null, text: '' });
        draftDirtyRef.current = false;
      }
      setSending(false);
      return;
    }
    setSending(true);
    setStatus('');
    try {
      const res = await apiFetch('/api/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: selectedChat.contact, text, reply_to: replyTo || undefined })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar');
      const now = new Date();
      const newMsg = { id: `tmp-${now.getTime()}`, cuerpo_texto: text, fecha: now.toISOString(), direccion: 'out' };
      nearBottomRef.current = true; // al enviar propio, siempre bajamos al fondo
      setMessages((prev) => [...prev, newMsg]);
      setComposer('');
      if (composerRef.current) composerRef.current.style.height = 'auto'; // el textarea vuelve a 1 línea
      setReplyTarget(null);
      // Responder MANUALMENTE descarta el Borrador IA (el backend /send también lo resuelve,
      // así no reaparece al refrescar). Evita además mandar el texto de la IA por error.
      setDraft({ id: null, text: '' });
      draftDirtyRef.current = false;
      setStatus('Enviado');
      // Reconciliar con el servidor (trae el mensaje real con wa_message_id/estado de entrega).
      fetchMessages(selectedContact, selectedConversationId, { silent: true });
      fetchChats({ silent: true });
    } catch (err) {
      setStatus(err.message);
    } finally {
      setSending(false);
    }
  };

  const uploadAndSendMedia = async (file, caption, replyTo) => {
    if (!file) return;
    if (!selectedChat) {
      setStatus('Selecciona un chat antes de enviar archivos');
      return false;
    }
    setUploadingFile(true);
    setStatus('Subiendo archivo...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await apiFetch('/api/uploads/whatsapp', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const uploadData = await parseJsonSafe(uploadRes);
      if (uploadRes.status === 401) return handleUnauthorized();
      if (!uploadRes.ok) throw new Error(uploadData.message || 'No se pudo subir el archivo');

      const sendRes = await apiFetch('/api/whatsapp/send-media', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: selectedChat.contact,
          storageName: uploadData.storageName,
          url: uploadData.url,
          filename: uploadData.originalName,
          mimeType: uploadData.mimeType,
          caption: caption || undefined,
          reply_to: replyTo || undefined
        })
      });
      const sendData = await parseJsonSafe(sendRes);
      if (sendRes.status === 401) return handleUnauthorized();
      if (!sendRes.ok) throw new Error(sendData.message || 'No se pudo enviar el archivo');
      setStatus('Archivo enviado');
      fetchMessages(selectedContact, selectedConversationId);
      fetchChats();
      return true;
    } catch (err) {
      setStatus(err.message || 'Error enviando archivo');
      return false;
    } finally {
      setUploadingFile(false);
    }
  };

  // Inserta el emoji en la posición del cursor del composer (no al final) y NO cierra el
  // popover, para poder poner varios seguidos.
  const insertEmoji = (emoji) => {
    const el = composerRef.current;
    if (!el) { setComposer((c) => `${c}${emoji}`); return; }
    const start = el.selectionStart ?? composer.length;
    const end = el.selectionEnd ?? composer.length;
    const next = composer.slice(0, start) + emoji + composer.slice(end);
    setComposer(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`; // reajusta el alto autoexpandible
      } catch (_e) { /* noop */ }
    });
  };

  // Emojis a mostrar según la categoría activa.
  const emojiItems = emojiCat === 'frequent'
    ? FREQUENT_EMOJIS
    : (EMOJI_CATEGORIES.find((c) => c.key === emojiCat)?.emojis || FREQUENT_EMOJIS);

  // Cerrar el popover de emojis al hacer click fuera o con Escape.
  useEffect(() => {
    if (!showEmojis) return;
    const onDown = (e) => { if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmojis(false); };
    const onKey = (e) => { if (e.key === 'Escape') setShowEmojis(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [showEmojis]);

  const fetchClients = async (term = '') => {
    setLinkModal((m) => ({ ...m, loading: true, error: '' }));
    try {
      const qs = new URLSearchParams();
      if (term) qs.append('search', term);
      const res = await apiFetch(`/api/whatsapp/link-search${qs.toString() ? `?${qs.toString()}` : ''}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) {
        setLinkModal((m) => ({ ...m, clients: data, loading: false }));
      } else {
        setLinkModal((m) => ({ ...m, loading: false, error: data.message || 'No se pudieron cargar opciones' }));
      }
    } catch (err) {
      setLinkModal((m) => ({ ...m, loading: false, error: err.message }));
    }
  };

  const openLinkModal = (chat) => {
    setLinkModal({
      open: true,
      conversationId: chat.conversationId,
      phone: chat.contact,
      contactName: chat.name || '',
      clientId: '',
      search: '',
      clients: [],
      loading: false,
      saving: false,
      error: ''
    });
    fetchClients();
  };

  // Envío real de la plantilla (con los valores de sus variables, si tiene)
  const doSendTemplate = async (tpl, params) => {
    if (tpl.type === 'custom') {
      return apiFetch(`/api/whatsapp/custom-templates/${tpl.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: selectedChat.contact, conversationId: selectedConversationId || undefined })
      });
    }
    const lang = (tpl.language || selectedTemplateLang || 'es').trim();
    return apiFetch('/api/whatsapp/send-menu', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        to: selectedChat.contact,
        template: tpl.name || undefined,
        language: lang,
        conversationId: selectedConversationId || undefined,
        params: params || undefined
      })
    });
  };

  const sendMenu = async () => {
    if (!selectedChat) return;
    const tpl = selectedTemplate;
    if (!tpl) {
      setStatus('Selecciona una plantilla');
      return;
    }
    // Si la plantilla lleva variables, primero se rellenan en el modal
    if (Array.isArray(tpl.variables) && tpl.variables.length) {
      setTemplateModal({ open: true, tpl, values: {}, sending: false, error: '' });
      return;
    }
    setSendingMenu(true);
    try {
      const res = await doSendTemplate(tpl, null);
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar la plantilla');
      setStatus('Plantilla enviada');
      fetchMessages(selectedContact, selectedConversationId);
      fetchChats();
    } catch (err) {
      setStatus(err.message);
    } finally {
      setSendingMenu(false);
    }
  };

  // Envío desde el modal, con las variables ya rellenadas
  const sendTemplateWithParams = async () => {
    const { tpl, values } = templateModal;
    if (!tpl) return;
    const faltan = (tpl.variables || []).filter((v) => !String(values[v] || '').trim());
    if (faltan.length) {
      setTemplateModal((m) => ({ ...m, error: `Rellena: ${faltan.join(', ')}` }));
      return;
    }
    setTemplateModal((m) => ({ ...m, sending: true, error: '' }));
    try {
      const res = await doSendTemplate(tpl, values);
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar la plantilla');
      setTemplateModal({ open: false, tpl: null, values: {}, sending: false, error: '' });
      setStatus('Plantilla enviada');
      fetchMessages(selectedContact, selectedConversationId);
      fetchChats();
    } catch (err) {
      setTemplateModal((m) => ({ ...m, sending: false, error: err.message }));
    }
  };

  const reprocessLatestAi = async () => {
    if (!latestAiMsg) return;
    try {
      const res = await apiFetch(`/api/whatsapp/messages/${latestAiMsg.id}/reprocess`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo reprocesar');
      setStatus('IA reprocesada');
      fetchMessages(selectedContact, selectedConversationId);
      fetchChats();
    } catch (err) {
      setStatus(err.message);
    }
    return null;
  };

  const deleteChat = async () => {
    if (!selectedConversationId) return;
    if (!window.confirm('¿Eliminar este chat y sus mensajes?')) return;
    try {
      const res = await apiFetch(`/api/whatsapp/conversations/${selectedConversationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar la conversación');
      setChats((prev) => prev.filter((c) => c.conversationId !== selectedConversationId));
      setMessages([]);
      setSelectedContact('');
      setSelectedConversationId('');
      setChatMenuOpen(false);
      fetchChats();
      setStatus('Conversación eliminada');
    } catch (err) {
      setStatus(err.message);
    }
  };

  const linkConversation = async () => {
    if (!linkModal.clientId) return setLinkModal((m) => ({ ...m, error: 'Selecciona un cliente' }));
    setLinkModal((m) => ({ ...m, saving: true, error: '' }));
    try {
      const res = await apiFetch(`/api/whatsapp/conversations/${linkModal.conversationId}/link-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          clientId: Number(linkModal.clientId),
          contactName: linkModal.contactName || linkModal.phone
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enlazar');
      setLinkModal((m) => ({ ...m, open: false, saving: false }));
      fetchChats();
      if (selectedContact) fetchMessages(selectedContact, selectedConversationId);
    } catch (err) {
      setLinkModal((m) => ({ ...m, saving: false, error: err.message }));
    }
  };

  const unlinkConversation = async (conversationId) => {
    if (!conversationId) return;
    try {
      const res = await apiFetch(`/api/whatsapp/conversations/${conversationId}/unlink`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo desenlazar');
      fetchChats();
      if (selectedContact) fetchMessages(selectedContact, selectedConversationId);
    } catch (err) {
      setStatus(err.message);
    }
  };

  const openChatOptions = async (conversationId) => {
    if (!conversationId) return;
    setChatOptionsModal((p) => ({ ...p, open: true, loading: true, conversationId }));
    try {
      const res = await apiFetch(`/api/whatsapp/conversations/${conversationId}/options`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudieron cargar opciones');
      setChatOptionsModal({
        open: true,
        conversationId,
        loading: false,
        disable_auto_reply: data.disable_auto_reply,
        ai_generate: data.ai_generate,
        ai_auto_send: data.ai_auto_send
      });
    } catch (err) {
      setChatOptionsModal((p) => ({ ...p, loading: false }));
      setStatus(err.message);
    }
  };

  const saveChatOptions = async () => {
    if (!chatOptionsModal.conversationId) return;
    try {
      const res = await apiFetch(`/api/whatsapp/conversations/${chatOptionsModal.conversationId}/options`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          disable_auto_reply: chatOptionsModal.disable_auto_reply,
          ai_generate: chatOptionsModal.ai_generate,
          ai_auto_send: chatOptionsModal.ai_auto_send
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudieron guardar opciones');
      setChatOptionsModal((p) => ({ ...p, open: false }));
      // refrescar chat seleccionado
      fetchChats();
    } catch (err) {
      setStatus(err.message);
    }
  };

  const showSidebar = !isMobile ? true : sidebarOpen;
  const showChat = !isMobile ? true : !sidebarOpen;
  const isChatSelected = (chat) => {
    if (selectedConversationId && chat?.conversationId) {
      return String(chat.conversationId) === String(selectedConversationId);
    }
    return chat?.contact === selectedContact;
  };

  return (
    <div className={`whatsapp-wrap shadow-sm ${isMobile ? 'mobile' : ''}`}>
      {showSidebar && (
      <div className={`whatsapp-sidebar ${showSidebar ? 'show' : ''}`}>
        <div className="whatsapp-sidebar-header">
          <div className="fw-bold wa-list-title">{isMobile ? 'WhatsApp' : 'WhatsApp Business'}</div>
          <div className="position-relative d-flex align-items-center gap-1">
            {isMobile && (
              <button className="btn btn-sm btn-light wa-header-icon" onClick={() => fetchChats()} title="Actualizar" aria-label="Actualizar chats">
                <BsArrowRepeat className={loadingChats ? 'spin' : ''} />
              </button>
            )}
            <button className="btn btn-sm btn-light wa-header-icon" onClick={() => setGlobalMenuOpen((o) => !o)} aria-label="Más opciones">
              <BsThreeDotsVertical />
            </button>
            {globalMenuOpen && (
              <div className="dropdown-menu show" style={{ right: 0, left: 'auto', top: 'calc(100% + 6px)' }}>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setGlobalMenuOpen(false);
                    navigate('/whatsapp/plantillas');
                  }}
                >
                  Plantillas
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setGlobalMenuOpen(false);
                    fetchChats();
                  }}
                >
                  Refrescar
                </button>
                {/* Ajustes de IA globales ahora están en Ajustes > WhatsApp */}
                <button
                  className="dropdown-item"
                  disabled={pushSubscribing || pushPermission === 'granted'}
                  onClick={async () => {
                    setGlobalMenuOpen(false);
                    const ok = await syncPushSubscription({ requestPermission: true });
                    if (ok) setStatus('Notificaciones push activadas');
                    else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
                      setStatus('Notificaciones bloqueadas. Habilítalas en el navegador del móvil.');
                    }
                  }}
                >
                  {pushSubscribing ? 'Activando notificaciones...' : (pushPermission === 'granted' ? 'Notificaciones push activas' : 'Activar notificaciones push')}
                </button>
                <button
                  className="dropdown-item"
                  onClick={async () => {
                    setGlobalMenuOpen(false);
                    await sendPushTest();
                  }}
                >
                  Enviar push de prueba
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="whatsapp-search">
          <BsSearch className="text-muted" />
          <input
            type="search"
            placeholder="Buscar por nombre, teléfono o mensaje"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="whatsapp-chat-list">
          <div className="chat-list-toolbar d-flex justify-content-between align-items-center px-3 py-2">
            {loadingChats ? <div className="text-muted small">Cargando chats...</div> : <div className="text-muted small">Chats</div>}
            <button className="btn btn-sm btn-light" onClick={fetchChats} title="Refrescar"><BsArrowRepeat /></button>
          </div>
          {isMobile && loadingChats && !chats.length && <div className="text-muted small px-3 py-3">Cargando chats...</div>}
          {/* Al refrescar se mantiene la lista visible (antes se vaciaba mientras cargaba) */}
          {(!loadingChats || chats.length > 0) && filteredChats.map((chat) => (
            <div
              key={chat.conversationId || chat.contact}
              className={`whatsapp-chat-item ${isChatSelected(chat) ? 'active' : ''}`}
              onClick={() => {
                const nextContact = chat.contact;
                const nextConversationId = chat.conversationId || '';
                const sameConversation = String(nextConversationId) === String(selectedConversationId || '');
                const sameContact = String(nextContact || '') === String(selectedContact || '');
                setSelectedContact(nextContact);
                setSelectedConversationId(nextConversationId);
                if (sameConversation && sameContact) {
                  fetchMessages(nextContact, nextConversationId);
                }
                markConversationSeen(nextConversationId, chat.lastDate);
                clearUnread(nextConversationId, chat.lastDate);
                if (isMobile) setSidebarOpen(false);
              }}
            >
              <div className={`avatar-placeholder ${chat.client_id ? 'linked' : 'unlinked'}`} title={chat.client_id ? 'Enlazado a cliente' : 'Sin enlazar'}>
                {(chat.name || chat.contact).slice(0, 2).toUpperCase()}
              </div>
              <div className="chat-info">
                <div className="wa-item-row">
                  <div className="wa-item-name">
                    {chat.contact_name ? `${chat.contact_name} - ${chat.name || chat.contact}` : (chat.name || chat.contact)}
                  </div>
                  <div className={`wa-item-time ${(chat.unread || 0) > 0 ? 'unread' : ''}`}>{listTimeOf(chat.lastDate)}</div>
                  <div className="d-none d-md-flex align-items-center gap-1 ms-1">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                      onClick={(e) => { e.stopPropagation(); openLinkModal(chat); }}
                    >
                      Enlazar
                    </button>
                    {chat.client_id && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                        onClick={(e) => { e.stopPropagation(); unlinkConversation(chat.conversationId); }}
                        title="Desenlazar"
                      >
                        <FiLink />
                      </button>
                    )}
                  </div>
                </div>
                <div className="wa-item-row">
                  <div className={`wa-item-preview ${(chat.unread || 0) > 0 ? 'unread' : ''}`}>
                    {(() => {
                      const kind = getMediaPlaceholderKind(chat.lastMessage);
                      if (!kind) return chat.lastMessage || '';
                      const label = getMediaPlaceholderLabel(kind);
                      const icon = kind === 'image'
                        ? <BsImage />
                        : (kind === 'audio' ? <BsVolumeUp /> : (kind === 'video' ? <BsCameraVideo /> : <BsFileEarmark />));
                      return (
                        <span className="chat-lastmedia" title={label}>
                          {icon}
                          <span>{label}</span>
                        </span>
                      );
                    })()}
                  </div>
                  {chat.status && chat.status !== 'abierta' && (
                    <span className="wa-item-status">{formatConversationStatus(chat.status).label}</span>
                  )}
                  {(chat.unread || 0) > 0 && <span className="badge rounded-pill wa-unread-badge">{chat.unread}</span>}
                </div>
              </div>
            </div>
          ))}
          {!loadingChats && !filteredChats.length && (
            <div className="p-3 text-muted small">
              Sin conversaciones.
              <div className="mt-1">Las conversaciones aparecen automáticamente al recibir mensajes. Verifica la configuración del webhook en Ajustes → WhatsApp.</div>
            </div>
          )}
        </div>
      </div>
      )}
      {showChat && (
      <div className={`whatsapp-chat ${draft.id ? 'has-draft' : ''}`}>
        <div className="whatsapp-chat-header">
          {isMobile ? (
            <>
              <button type="button" className="wa-back-btn" onClick={() => setSidebarOpen(true)} aria-label="Chats">
                <BsChevronLeft />
              </button>
              {selectedChat && (
                <div className={`avatar-placeholder mini ${selectedChat.client_id ? 'linked' : 'unlinked'}`}>
                  {(selectedChat.name || selectedChat.contact).slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="wa-chat-title">
                {selectedChat?.client_id ? (
                  <button
                    type="button"
                    className="btn btn-link p-0 fw-bold text-truncate"
                    onClick={() => navigate(`/clientes/${selectedChat.client_id}`)}
                    title="Abrir ficha del cliente"
                  >
                    {displayName || 'Selecciona un chat'}
                  </button>
                ) : (
                  <div className="fw-bold text-truncate">{displayName || 'Selecciona un chat'}</div>
                )}
                <div className="text-muted small text-truncate">{selectedChat ? `+${selectedChat.contact}` : ''}</div>
              </div>
              <div className="wa-chat-actions position-relative">
                <button className="btn btn-sm btn-light wa-header-icon" onClick={() => setChatMenuOpen((o) => !o)} aria-label="Acciones del chat" disabled={!selectedChat}>
                  <BsThreeDotsVertical />
                </button>
                {chatMenuOpen && selectedChat && (
                  <>
                    <div className="wa-menu-backdrop" onClick={() => setChatMenuOpen(false)} />
                    <div className="dropdown-menu show wa-chat-menu" style={{ right: 0, left: 'auto', top: 'calc(100% + 6px)' }}>
                      <button className="dropdown-item" onClick={() => { setChatMenuOpen(false); setTemplatePickerOpen(true); }}>
                        <BsFileText className="me-2" />Enviar plantilla
                      </button>
                      {latestAiMsg && (
                        <button className="dropdown-item" onClick={() => { setChatMenuOpen(false); reprocessLatestAi(); }}>
                          <BsRobot className="me-2" />Volver a procesar con IA
                        </button>
                      )}
                      {selectedChat.client_id ? (
                        <>
                          <button className="dropdown-item" onClick={() => { setChatMenuOpen(false); navigate(`/clientes/${selectedChat.client_id}`); }}>
                            <BsPersonLinesFill className="me-2" />Ver ficha del cliente
                          </button>
                          <button className="dropdown-item" onClick={() => { setChatMenuOpen(false); openLinkModal(selectedChat); }}>
                            <BsLink45Deg className="me-2" />Cambiar cliente enlazado
                          </button>
                          <button className="dropdown-item" onClick={() => { setChatMenuOpen(false); unlinkConversation(selectedChat.conversationId); }}>
                            <FiLink className="me-2" />Desenlazar del cliente
                          </button>
                        </>
                      ) : (
                        <button className="dropdown-item" onClick={() => { setChatMenuOpen(false); openLinkModal(selectedChat); }}>
                          <BsLink45Deg className="me-2" />Enlazar a un cliente
                        </button>
                      )}
                      <button className="dropdown-item" onClick={() => { setChatMenuOpen(false); openChatOptions(selectedConversationId); }}>
                        <BsGear className="me-2" />Opciones del chat
                      </button>
                      <div className="dropdown-divider" />
                      <button className="dropdown-item text-danger" onClick={() => { setChatMenuOpen(false); deleteChat(); }}>
                        <BsTrash className="me-2" />Eliminar chat
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                {selectedChat?.client_id ? (
                  <button
                    type="button"
                    className="btn btn-link p-0 fw-bold"
                    onClick={() => navigate(`/clientes/${selectedChat.client_id}`)}
                    title="Abrir ficha del cliente"
                  >
                    {displayName || 'Selecciona un chat'}
                  </button>
                ) : (
                  <div className="fw-bold">{displayName || 'Selecciona un chat'}</div>
                )}
                <div className="text-muted small">{selectedChat ? selectedChat.contact : ''}</div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center gap-1">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 180 }}
                    value={selectedTemplateId}
                    onChange={(e) => {
                      setSelectedTemplateId(e.target.value);
                      const tpl = templates.find((x) => String(x.id) === String(e.target.value));
                      if (tpl && tpl.type !== 'custom') {
                        setSelectedTemplateLang(tpl.language || 'es');
                      }
                    }}
                  >
                    <option value="">Plantilla</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.type === 'custom' ? '[Propia] ' : '[META] '}{t.name} ({t.language})
                      </option>
                    ))}
                  </select>
                  {(!selectedTemplate || selectedTemplate.type === 'custom') && (
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 90 }}
                      value={selectedTemplateLang}
                      onChange={(e) => setSelectedTemplateLang(e.target.value)}
                    >
                      {templateLanguageOptions.map((lang) => (
                        <option key={lang} value={lang}>
                          {lang}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {selectedChat && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={sendMenu}
                    disabled={sendingMenu}
                  >
                    {sendingMenu ? 'Enviando...' : 'Enviar plantilla'}
                  </button>
                )}
                {latestAiMsg && (
                  <button className="btn btn-sm btn-outline-primary" onClick={reprocessLatestAi}>
                    Procesar IA
                  </button>
                )}
                <div className="position-relative">
                  <button className="btn btn-sm btn-light" onClick={() => setChatMenuOpen((o) => !o)}>
                    <BsThreeDotsVertical />
                  </button>
                  {chatMenuOpen && (
                    <div className="dropdown-menu show" style={{ right: 0, left: 'auto', top: 'calc(100% + 6px)' }}>
                      <button
                        className="dropdown-item text-danger"
                        onClick={() => {
                          setChatMenuOpen(false);
                          deleteChat();
                        }}
                      >
                        Eliminar chat
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          setChatMenuOpen(false);
                          openChatOptions(selectedConversationId);
                        }}
                      >
                        Opciones
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div
          className="whatsapp-messages"
          ref={messagesScrollRef}
          onScroll={(e) => {
            // Si el usuario sube a leer el historial, nada (borrador, teclado, refresco) le baja de golpe
            const c = e.currentTarget;
            nearBottomRef.current = (c.scrollHeight - c.scrollTop - c.clientHeight) < 140;
          }}
        >
          {loadingMessages && <div className="text-muted small">Cargando mensajes...</div>}
	          {!loadingMessages && messages.map((msg, idx) => {
	            const isOut = msg.direccion === 'out' || msg.direccion === 'salida' || msg.direccion === 'outbound' || msg.direccion === 'out';
	            // La fecha va en el separador de día; en la burbuja basta la hora
	            const time = msg.fecha
	              ? new Date(msg.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
	              : '';
	            const dayKey = dayKeyOf(msg.fecha);
	            const showDaySep = Boolean(dayKey) && (idx === 0 || dayKeyOf(messages[idx - 1]?.fecha) !== dayKey);
	            const rawBody = (msg.cuerpo_texto || msg.text || '').toString();
	            const transcriptText = getTranscriptText(rawBody);
            const tipo = msg.tipo || msg.type || '';
            const isReaction = tipo === 'reaction';
            const contactSummary = getContactSummaryFromPayload(msg.raw_payload);
            const contextId = msg.context_wa_message_id;
	            const contextMsg = contextId ? messageByWaId.get(contextId) : null;
	            const contextPreview = contextMsg ? resolveMessagePreview(contextMsg) : (contextId ? 'Mensaje anterior' : '');
	            const placeholderKind = !isReaction ? getMediaPlaceholderKind(rawBody) : null;
	            const mediaSource = msg.media_local_url || msg.media_url;
	            const mediaUrl = toAbsoluteMediaUrl(mediaSource);
	            const downloadUrl = buildMediaDownloadUrl(mediaSource, msg.media_filename || '');
	            const shouldHideBody = !isReaction && ((Boolean(mediaUrl) && Boolean(placeholderKind)) || Boolean(transcriptText));
	            const prettyBody = shouldHideBody ? '' : prettifyMediaPlaceholderText(rawBody, { contactSummary });
	            const bodyToRender = isReaction
	              ? (rawBody || '👍')
              : (isGenericBracketPlaceholder(prettyBody) ? (getButtonTextFromPayload(msg.raw_payload) || prettyBody) : prettyBody);
            const renderMedia = () => {
              if (isReaction) return null;
              if (!mediaUrl) return null;
                const mime = (msg.media_mime || '').toLowerCase();
                if (mime.startsWith('audio')) {
                  const audioSource = buildPlayableAudioSource(mediaUrl, msg.media_mime || '');
                  return (
                    <div className="attachment-card">
                      <div className="attachment-icon"><BsPlay /></div>
                      <div className="attachment-body">
                        <div className="fw-bold small">Audio</div>
                        <audio
                          key={audioSource.src}
                          controls
                          preload="none"
                          style={{ width: '100%' }}
                          // Google Drive no envía cabeceras CORS para audio; evitamos crossOrigin para permitir reproducción
                        >
                          <source src={audioSource.src} type={audioSource.type} />
                        </audio>
	                        <a className="btn btn-sm btn-outline-secondary mt-2" href={downloadUrl || mediaUrl} download={msg.media_filename || true}>
	                          <BsDownload className="me-1" />
	                          Descargar
	                        </a>
                    </div>
                  </div>
                );
              }
                if (mime.startsWith('video')) {
                  return (
                    <div className="attachment-card">
                      <div className="attachment-icon"><BsPlay /></div>
                      <div className="attachment-body">
                        <div className="fw-bold small">Vídeo</div>
                        <video
                          controls
                          preload="none"
                          src={mediaUrl}
                          style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }}
                          // crossOrigin no es necesario y puede bloquear si el host no expone CORS
                          type={msg.media_mime || undefined}
                        />
	                        <a className="btn btn-sm btn-outline-secondary mt-2" href={downloadUrl || mediaUrl} download={msg.media_filename || true}>
	                          <BsDownload className="me-1" />
	                          Descargar
	                        </a>
                      </div>
                    </div>
                  );
                }
            if (mime.startsWith('image')) {
              return (
                <div className="attachment-card">
                  <img src={mediaUrl} alt={msg.media_filename || 'Imagen'} style={{ maxWidth: '100%', borderRadius: 6 }} />
                  <div className="mt-2">
	                    <a className="btn btn-sm btn-outline-secondary" href={downloadUrl || mediaUrl} download={msg.media_filename || true}>
	                      <BsDownload className="me-1" />
	                      Descargar
	                    </a>
                  </div>
                </div>
              );
            }
            return (
              <div className="attachment-card">
                  <div className="attachment-icon"><BsFileEarmark /></div>
                  <div className="attachment-body">
                    <div className="fw-bold small">{msg.media_filename || 'Archivo'}</div>
	                    <a className="btn btn-sm btn-outline-secondary mt-1" href={downloadUrl || mediaUrl} download={msg.media_filename || true}>
	                      <BsDownload className="me-1" />
	                      Descargar
	                    </a>
                  </div>
                </div>
              );
            };
            const outStatus = isOut ? (msg.wa_status || '').toLowerCase() : '';
            // Ticks de estado al estilo WhatsApp: gris=enviado/entregado, azul=leído, rojo=fallo.
            const renderTicks = () => {
              if (!isOut) return null;
              if (outStatus === 'failed') return <BsExclamationCircle style={{ color: '#dc3545' }} title="No entregado" />;
              if (outStatus === 'read') return <BsCheck2All className="tick-read" title="Leído" />;
              if (outStatus === 'delivered') return <BsCheck2All title="Entregado" />;
              if (outStatus === 'sent') return <BsCheck2 title="Enviado" />;
              return <BsClock title="Enviando" />;
            };
            const renderStatus = () => {
              if (!isOut || outStatus !== 'failed') return null;
              const details = msg.wa_error_message || 'No entregado';
              return (
                <div className="text-danger small mt-1">
                  Falló: {details}
                </div>
              );
            };
            const canReply = Boolean(msg.wa_message_id);
            const replyPreview = resolveMessagePreview(msg);
            return (
	                <React.Fragment key={msg.id}>
	                {showDaySep && (
	                  <div className="wa-day-sep"><span>{dayLabelOf(msg.fecha)}</span></div>
	                )}
	                <div className={`whatsapp-message ${isOut ? 'out' : 'in'}`}>
	                <div className="bubble">
                    {contextPreview && (
                      <div className="reply-context">
                        <div className="reply-label">{isReaction ? 'Reaccionó a' : 'Respuesta a'}</div>
                        <div className="reply-snippet">{contextPreview}</div>
                      </div>
                    )}
	                  {msg.ia_intent && (
	                    <div className="badge text-uppercase mb-1 wa-intent" style={{ fontSize: '0.65rem' }}>
	                      {msg.ia_intent} {msg.ia_confidence ? `(${Math.round(msg.ia_confidence * 100)}%)` : ''}
	                    </div>
	                  )}
	                  {bodyToRender && (
	                    <div className="message-text" style={{ whiteSpace: 'pre-wrap' }}>{bodyToRender}</div>
	                  )}
	                  {renderMedia()}
	                  {transcriptText && (
	                    <div className="message-text mt-1" style={{ whiteSpace: 'pre-wrap' }}>
	                      <span className="small text-muted d-block">🎙️ Transcripción del audio</span>
	                      {transcriptText}
	                    </div>
	                  )}
	                  <div className="meta text-muted small">
	                    <span>{time} {renderTicks()}</span>
                      {canReply && (
                        <button
                          type="button"
                          className="reply-btn"
                          onClick={() => setReplyTarget({ wa_message_id: msg.wa_message_id, preview: replyPreview || 'Mensaje' })}
                          title="Responder"
                        >
                          <BsReply />
                          <span className="reply-text">Responder</span>
                        </button>
                      )}
	                  </div>
                    {renderStatus()}
	                </div>
	              </div>
	                </React.Fragment>
	            );
          })}
          <div className="wa-bottom-spacer"></div>
          <div ref={messagesEndRef}></div>
          {!loadingMessages && !messages.length && (
            <div className="text-muted small">
              {selectedChat?.status === 'cerrada'
                ? 'Conversación cerrada. No hay mensajes visibles.'
                : selectedChat?.status === 'eliminada'
                  ? 'Conversación eliminada.'
                  : 'Sin mensajes aún.'}
            </div>
          )}
        </div>
        {draft.id && (
          <div className={`border-top ai-draft ${draftCollapsed ? 'collapsed' : ''}`}>
            <button type="button" className="ai-draft-head" onClick={() => setDraftCollapsed((c) => !c)} aria-expanded={!draftCollapsed}>
              <span className="ai-draft-title"><BsRobot className="me-1" />Borrador IA</span>
              {draftCollapsed
                ? <span className="ai-draft-peek">{draft.text}</span>
                : <span className="text-muted small d-none d-md-inline">Generado automáticamente, revisa antes de enviar</span>}
              {draftCollapsed ? <BsChevronUp className="ms-auto flex-shrink-0" /> : <BsChevronDown className="ms-auto flex-shrink-0" />}
            </button>
            {!draftCollapsed && (
            <div className="ai-draft-body">
            <textarea className="form-control mb-2" rows="3" value={draft.text} onChange={(e) => { draftDirtyRef.current = true; setDraft((d) => ({ ...d, text: e.target.value })); }} />
            {(draft.actions || []).length > 0 && (
              <div className="mb-2 small">
                <div className="text-muted mb-1">Acciones propuestas (se ejecutan al aprobar las que dejes marcadas):</div>
                {draft.actions.map((a, i) => (
                  <div className="form-check" key={i}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`wa-action-${i}`}
                      checked={(draft.selected || []).includes(i)}
                      onChange={(e) => {
                        draftDirtyRef.current = true;
                        setDraft((d) => ({ ...d, selected: e.target.checked ? [...(d.selected || []), i] : (d.selected || []).filter((x) => x !== i) }));
                      }}
                    />
                    <label className="form-check-label" htmlFor={`wa-action-${i}`}>{describeAction(a)}</label>
                  </div>
                ))}
              </div>
            )}
            <div className="d-flex gap-2 ai-draft-actions">
              <button
                className="btn btn-primary btn-sm"
                disabled={!draft.text || sending}
                onClick={async () => {
                  if (!draft.id) return;
                  try {
                    const res = await apiFetch(`/api/whatsapp/messages/${draft.id}/decision`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ action: 'approve', body: draft.text, actions: (draft.selected || []) })
                    });
                    const data = await parseJsonSafe(res);
	                    if (res.status === 401) return handleUnauthorized();
	                    if (!res.ok) throw new Error(data.message || 'No se pudo aprobar');
	                    setDraft({ id: null, text: '', actions: [], selected: [] });
	                    const acts = Array.isArray(data.actions) ? data.actions : [];
	                    setStatus(acts.length
	                      ? `Borrador enviado · ${acts.map((r) => `${r.ok ? '✓' : '✗'} ${r.detail}`).join(' · ')}`
	                      : 'Borrador enviado');
	                    draftDirtyRef.current = false;
	                    fetchMessages(selectedContact, selectedConversationId, { silent: true });
	                    fetchChats({ silent: true });
	                  } catch (err) {
	                    setStatus(err.message);
                  }
                }}
              >
                Aprobar y enviar
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={async () => {
                  if (!draft.id) return;
                  try {
                    const res = await apiFetch(`/api/whatsapp/messages/${draft.id}/decision`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ action: 'reject' })
                    });
                    const data = await parseJsonSafe(res);
                    if (res.status === 401) return handleUnauthorized();
                    if (!res.ok) throw new Error(data.message || 'No se pudo rechazar');
                    setDraft({ id: null, text: '', actions: [], selected: [] });
                    setStatus('Borrador rechazado');
                    draftDirtyRef.current = false;
                    fetchMessages(selectedContact, selectedConversationId, { silent: true });
                    fetchChats({ silent: true });
                  } catch (err) {
                    setStatus(err.message);
                  }
                }}
              >
                Rechazar
              </button>
            </div>
            </div>
            )}
          </div>
        )}
        {replyTarget && (
          <div className="reply-preview">
            <div>
              <div className="reply-preview-label">Respondiendo</div>
              <div className="reply-preview-text">{replyTarget.preview || 'Mensaje'}</div>
            </div>
            <button
              type="button"
              className="reply-cancel"
              onClick={() => setReplyTarget(null)}
            >
              Cancelar
            </button>
          </div>
        )}
      <div className="whatsapp-composer">
        {uploadingFile && <div className="uploading-indicator text-muted small">Subiendo archivo...</div>}
        {pendingFile && (
          <div className="uploading-indicator text-muted small">
            Archivo listo: {pendingFile.name}
            <button
              type="button"
              className="btn btn-link btn-sm ms-2 p-0"
              onClick={() => {
                setPendingFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              Quitar
            </button>
          </div>
        )}
        <div className="position-relative" ref={emojiRef}>
          <button type="button" className="icon-btn" aria-label="Emojis" aria-expanded={showEmojis} onClick={() => setShowEmojis((s) => !s)}>
            <BsEmojiSmile />
          </button>
          {showEmojis && (
            <div className="emoji-popover" role="dialog" aria-label="Selector de emojis">
              <div className="emoji-cats">
                <button type="button" className={`emoji-cat ${emojiCat === 'frequent' ? 'active' : ''}`} onClick={() => setEmojiCat('frequent')} title="Frecuentes">🕘</button>
                {EMOJI_CATEGORIES.map((c) => (
                  <button key={c.key} type="button" className={`emoji-cat ${emojiCat === c.key ? 'active' : ''}`} onClick={() => setEmojiCat(c.key)} title={c.label}>{c.icon}</button>
                ))}
              </div>
              <div className="emoji-grid">
                {emojiItems.map((e, i) => (
                  <button key={`${e}-${i}`} type="button" className="emoji-btn" onClick={() => insertEmoji(e)}>{e}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button type="button" className="icon-btn" onClick={() => fileInputRef.current?.click()} title="Adjuntar archivo" aria-label="Adjuntar archivo" disabled={!selectedChat}>
          <BsPaperclip />
        </button>
        {isMobile && (
          <button type="button" className="icon-btn" onClick={() => setTemplatePickerOpen(true)} title="Enviar plantilla" aria-label="Enviar plantilla" disabled={!selectedChat}>
            <BsFileText />
          </button>
        )}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setPendingFile(file);
            setStatus('Archivo listo para enviar');
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
        <textarea
          ref={composerRef}
          className="composer-input"
          rows={1}
          placeholder={selectedChat ? 'Escribe un mensaje' : 'Selecciona un chat'}
          value={composer}
          disabled={!selectedChat}
          onChange={(e) => {
            setComposer(e.target.value);
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
          }}
          onKeyDown={(e) => {
            // Enter envía; Shift+Enter salto de línea. No enviar durante composición IME (acentos).
            // En el móvil, como en WhatsApp, Enter es salto de línea y se envía con el botón.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !isMobile) {
              e.preventDefault();
              sendMessage();
              if (composerRef.current) composerRef.current.style.height = 'auto';
            }
          }}
        />
        <button
          type="button"
          className="icon-btn send-btn"
          aria-label="Enviar mensaje"
          onClick={sendMessage}
          disabled={!selectedChat || sending || uploadingFile || (!composer.trim() && !pendingFile)}
        >
          <BsFillSendFill />
        </button>
      </div>
        {status && <div className="wa-status" role="status">{status}</div>}
      </div>
      )}

      {chatOptionsModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Opciones del chat</h5>
                <button className="btn-close" onClick={() => setChatOptionsModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <div className="modal-body">
                {chatOptionsModal.loading ? (
                  <p className="text-muted">Cargando...</p>
                ) : (
                  <>
                    <div className="alert alert-light border py-2 small mb-3">
                      <div className="fw-semibold mb-1">Ajustes globales (referencia)</div>
                      <div>Borrador IA: <strong>{aiConfig.generateDraft ? 'Sí' : 'No'}</strong> · Auto-envío: <strong>{aiConfig.autoSend ? 'Sí' : 'No'}</strong></div>
                      <div className="text-muted">Lo de abajo aplica solo a esta conversación y prevalece sobre los ajustes globales. Cámbialos en Ajustes → WhatsApp.</div>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="disableAutoReplyChat"
                        checked={chatOptionsModal.disable_auto_reply}
                        onChange={(e) => setChatOptionsModal((p) => ({ ...p, disable_auto_reply: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="disableAutoReplyChat">Deshabilitar envío de plantillas automáticas</label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="aiDraftChat"
                        checked={chatOptionsModal.ai_generate}
                        onChange={(e) => setChatOptionsModal((p) => ({ ...p, ai_generate: e.target.checked, ai_auto_send: e.target.checked ? p.ai_auto_send : false }))}
                      />
                      <label className="form-check-label" htmlFor="aiDraftChat">Generar borrador IA automáticamente</label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="aiSendChat"
                        disabled={!chatOptionsModal.ai_generate}
                        checked={chatOptionsModal.ai_generate && chatOptionsModal.ai_auto_send}
                        onChange={(e) => setChatOptionsModal((p) => ({ ...p, ai_auto_send: e.target.checked && p.ai_generate }))}
                      />
                      <label className="form-check-label" htmlFor="aiSendChat">Enviar respuesta IA automáticamente</label>
                    </div>
                    <div className="form-text mt-2">Estas opciones tienen preferencia sobre la configuración general.</div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setChatOptionsModal((p) => ({ ...p, open: false }))}>Cerrar</button>
                <button className="btn btn-primary" onClick={saveChatOptions} disabled={chatOptionsModal.loading}>Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {linkModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enlazar a cliente/lead</h5>
                <button className="btn-close" onClick={() => setLinkModal((m) => ({ ...m, open: false }))}></button>
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <button
                    className="btn btn-outline-primary w-100"
                    onClick={async () => {
                      setLinkModal((m) => ({ ...m, loading: true, error: '' }));
                      try {
                        const res = await apiFetch(`/api/whatsapp/conversations/${linkModal.conversationId}/match-existing`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        const data = await parseJsonSafe(res);
	                        if (res.status === 401) return handleUnauthorized();
	                        if (!res.ok) throw new Error(data.message || 'No se encontró coincidencia');
	                        setLinkModal((m) => ({ ...m, loading: false, open: false }));
	                        fetchChats();
	                        if (selectedContact) fetchMessages(selectedContact, selectedConversationId);
	                      } catch (err) {
	                        setLinkModal((m) => ({ ...m, loading: false, error: err.message }));
	                      }
                    }}
                  >
                    Enlazar automáticamente por teléfono
                  </button>
                </div>
                <div className="mb-2">
                  <label className="form-label small">Selecciona cliente (escribe para buscar)</label>
                  <Select
                    classNamePrefix="select"
                    isClearable
                    isLoading={linkModal.loading}
                    options={linkModal.clients.map((c) => {
                      const label =
                        c.type === 'contact'
                          ? `${c.contact_name || 'Contacto'} · ${c.client_name || ''}${c.ciudad ? ` - ${c.ciudad}` : ''}`
                          : `${c.client_name}${c.tipo ? ` (${c.tipo})` : ''}${c.ciudad ? ` - ${c.ciudad}` : ''}`;
                      return { value: c.client_id, label, contactName: c.contact_name || '', type: c.type };
                    })}
                    placeholder="Busca y selecciona cliente o contacto"
                    value={
                      linkModal.clientId
                        ? linkModal.clients
                            .map((c) => {
                              const label =
                                c.type === 'contact'
                                  ? `${c.contact_name || 'Contacto'} · ${c.client_name || ''}${c.ciudad ? ` - ${c.ciudad}` : ''}`
                                  : `${c.client_name}${c.tipo ? ` (${c.tipo})` : ''}${c.ciudad ? ` - ${c.ciudad}` : ''}`;
                              return { value: c.client_id, label, contactName: c.contact_name || '', type: c.type };
                            })
                            .find((o) => String(o.value) === String(linkModal.clientId)) || null
                        : null
                    }
                    onChange={(opt) =>
                      setLinkModal((m) => ({
                        ...m,
                        clientId: opt ? opt.value : '',
                        contactName: opt?.contactName || m.contactName
                      }))
                    }
                    onInputChange={(val) => {
                      if (val && val.length >= 2) fetchClients(val);
                      if (!val) fetchClients('');
                    }}
                  />
                  <div className="form-text">Escribe para buscar en clientes recientes.</div>
                </div>
                <div className="mb-2">
                  <label className="form-label small">Nombre del contacto</label>
                  <input
                    className="form-control"
                    value={linkModal.contactName}
                    onChange={(e) => setLinkModal((m) => ({ ...m, contactName: e.target.value }))}
                    placeholder="Ej: Juan Pérez"
                  />
                  <div className="form-text">Se asociará el teléfono {linkModal.phone}.</div>
                </div>
                {linkModal.error && <div className="alert alert-danger py-2">{linkModal.error}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setLinkModal((m) => ({ ...m, open: false }))}>Cerrar</button>
                <button className="btn btn-primary" disabled={linkModal.saving} onClick={linkConversation}>
                  {linkModal.saving ? 'Guardando...' : 'Enlazar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Variables de una plantilla META antes de enviarla, con vista previa del mensaje real */}
      {templatePickerOpen && (
        <div className="modal fade show wa-sheet" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}
             onClick={(e) => { if (e.target === e.currentTarget) setTemplatePickerOpen(false); }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><BsFileText className="me-2" />Enviar plantilla</h5>
                <button className="btn-close" onClick={() => setTemplatePickerOpen(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-2">
                  Fuera de las 24 horas desde el último mensaje del cliente, WhatsApp solo permite escribirle con una plantilla aprobada.
                </p>
                <select
                  className="form-select mb-2"
                  value={selectedTemplateId}
                  onChange={(e) => {
                    setSelectedTemplateId(e.target.value);
                    const tpl = templates.find((x) => String(x.id) === String(e.target.value));
                    if (tpl && tpl.type !== 'custom') setSelectedTemplateLang(tpl.language || 'es');
                  }}
                >
                  <option value="">Elige una plantilla</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.type === 'custom' ? '[Propia] ' : ''}{t.name} ({t.language})</option>
                  ))}
                </select>
                {selectedTemplate && selectedTemplate.type === 'custom' && (
                  <select className="form-select mb-2" value={selectedTemplateLang} onChange={(e) => setSelectedTemplateLang(e.target.value)}>
                    {templateLanguageOptions.map((lang) => <option key={lang} value={lang}>{lang}</option>)}
                  </select>
                )}
                {selectedTemplate && selectedTemplate.body && (
                  <div className="wa-tpl-preview">{selectedTemplate.body}</div>
                )}
                {selectedTemplate && Array.isArray(selectedTemplate.variables) && selectedTemplate.variables.length > 0 && (
                  <div className="text-muted small mt-2">En el siguiente paso rellenarás: {selectedTemplate.variables.join(', ')}.</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setTemplatePickerOpen(false)}>Cancelar</button>
                <button
                  className="btn btn-primary"
                  disabled={!selectedTemplate || sendingMenu}
                  onClick={() => { setTemplatePickerOpen(false); sendMenu(); }}
                >
                  {sendingMenu ? 'Enviando...' : (selectedTemplate && Array.isArray(selectedTemplate.variables) && selectedTemplate.variables.length ? 'Continuar' : 'Enviar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {templateModal.open && templateModal.tpl && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}
             onClick={(e) => { if (e.target === e.currentTarget) setTemplateModal((m) => ({ ...m, open: false })); }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar plantilla · {templateModal.tpl.name}</h5>
                <button className="btn-close" onClick={() => setTemplateModal((m) => ({ ...m, open: false }))}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small">
                  Rellena los datos que pide la plantilla. WhatsApp no permite saltos de línea dentro de una
                  variable: si pegas un texto de varias líneas (por ejemplo, los datos de una reunión de Google
                  Meet) se unirán en una sola separadas por «·». Debajo ves el mensaje tal y como le llegará al cliente.
                </p>
                {(templateModal.tpl.variables || []).map((v) => (
                  <div className="mb-3" key={v}>
                    <label className="form-label">{/^\d+$/.test(String(v)) ? `Variable ${v}` : v}</label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={templateModal.values[v] || ''}
                      placeholder={String(v).toLowerCase().includes('reunion')
                        ? 'Pega aquí los datos de la reunión de Google Meet'
                        : 'Escribe el texto de esta variable'}
                      onChange={(e) => setTemplateModal((m) => ({ ...m, values: { ...m.values, [v]: e.target.value }, error: '' }))}
                    />
                  </div>
                ))}
                <label className="form-label fw-bold">Vista previa</label>
                <div className="border rounded p-3 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
                  {(() => {
                    let txt = String(templateModal.tpl.body || '');
                    (templateModal.tpl.variables || []).forEach((v) => {
                      txt = txt.split(`{{${v}}}`).join(flattenParam(templateModal.values[v] || `«${v}»`));
                    });
                    return txt;
                  })()}
                </div>
                {templateModal.error && <div className="alert alert-danger py-2 mt-3 mb-0">{templateModal.error}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setTemplateModal((m) => ({ ...m, open: false }))}>Cancelar</button>
                <button className="btn btn-primary" disabled={templateModal.sending} onClick={sendTemplateWithParams}>
                  {templateModal.sending ? 'Enviando...' : 'Enviar plantilla'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function WhatsappPage() {
  const location = useLocation();
  const channel = location.pathname.replace(/\/$/, '') === '/whatsapp/qr' ? 'qr' : 'cloud';
  return <div className="whatsapp-page">
    <nav className="whatsapp-channels" aria-label="Conexiones de WhatsApp">
      <Link className={`btn ${channel === 'cloud' ? 'btn-primary' : 'btn-outline-primary'}`} aria-current={channel === 'cloud' ? 'page' : undefined} to="/whatsapp">WhatsApp Business API</Link>
      <Link className={`btn ${channel === 'qr' ? 'btn-primary' : 'btn-outline-primary'}`} aria-current={channel === 'qr' ? 'page' : undefined} to="/whatsapp/qr">WhatsApp por QR</Link>
    </nav>
    {channel === 'cloud' ? <WhatsappCloudPage /> : <WhatsappQrPanel />}
  </div>;
}
